<template>
    <div>
        <div v-show="expanded" class="form-bordered">
            <div class="alert alert-danger" v-if="!isActive">
              <strong>This payment option is not active—new registrants cannot sign up for it.</strong>
              Existing registrations are not affected by this setting.
            </div>
            <div class="form-group">
                <label for="option_name">Name</label>
                <input id="option_name" v-model="name" type="text" class="form-control">
            </div>
            <div class="form-group">
                <label>Allowed payment methods</label>
                <select v-model="allowedPaymentMethods" class="form-control" name="payment_method">
                    <option selected disabled>Choose payment method...</option>
                    <option value="card">Credit or debit only</option>
                    <option value="all">All (cash, check, credit/debit)</option>
                </select>
            </div>
            <div class="form-group" v-if="!allowClassPass">
                <label>One time charge or installments?</label>
                <select v-model="paymentStrategy" class="form-control" name="payment_strategy">
                    <option selected disabled>Choose payment strategy...</option>
                    <option value="once">One time charge</option>
                    <option value="installments">Installments</option>
                </select>
            </div>
            <template v-if="oneTimeCharge">
                <div class="form-group">
                    <label>Price</label>
                    <money v-model="price" v-bind="money" class="form-control"></money>
                </div>
            </template>
            <template v-else>
                <div class="form-group">
                    <label>
                        Payment Dates
                    </label>
                    <div class="grid grid-cols-1 gap-sm">
                      <div v-for="(installment, index) in installmentDates" :key="installment.clientId" class="bg-gray-50 border border-solid border-gray-100 p-sm rounded-xs w-fit">
                        <div class="flex align-items-center justify-content-between gap-sm">
                          <div class="font-semi">
                            Payment #{{index + 1}}
                          </div>
                          <a class="text-danger" @click.prevent="removeInstallment(index)" title="Remove this installment">
                            <i class="fa-solid fa-xmark"></i> Remove
                          </a>
                        </div>
                        <div class="flex flex-wrap align-items-center gap-sm m-t-xs">
                          <div class="form-group">
                            <label class="font-normal">Amount</label>
                            <money v-model="installment.price" v-bind="money" class="form-control"></money>
                          </div>
                          <div class="form-group">
                            <label class="font-normal">Payment Date</label>
                            <div>
                              <date-picker v-model="installment.date" :date="installment.date"></date-picker>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="card-bordered-dashed m-t-xs">
                        <a @click.prevent="addNewInstallment"><i class="fa-solid fa-plus"></i> Installment date</a>
                      </div>
                      <div class="bg-gray-50 w-fit p-xs rounded-xs" v-if="installmentDates.length > 0">
                        <i class="fa-solid fa-circle-info m-r-xs"></i> Summary - {{ prettyStrategy }} {{ prettyPrice }}
                      </div>
                    </div>
                </div>
            </template>
            <div class="form-group" v-if="allowDeposit">
                <label>Application fee (optional)</label>
                <money v-model="depositAmount" v-bind="money" class="form-control"></money>
                <p class="text-gray-600 m-t-xs">
                    If set, registrants pay this fee upfront. Application fees will not be refunded if a team member is denied.
                </p>
            </div>
            <template v-if="allowClassPass">
                <div class="form-group">
                    <label>How many passes does this payment option grant the wrestler?</label>
                    <input ref="passCountInput" type="number" min="1" step="1" v-model.number="passCount" class="form-control">
                </div>
                <div class="form-group">
                    <label>How many days until passes expire? <span class="text-gray-600 font-normal">(optional)</span></label>
                    <input ref="passExpiresInput" type="number" min="1" step="1" v-model.number="passExpiresInDays" class="form-control" placeholder="Leave blank for no expiry">
                    <p class="text-gray-600 m-t-xs">
                        For trial windows (e.g. a 7-day trial pack) or limited drop-in promos. Each pass expires this many days after purchase. Leave blank for passes that never expire.
                    </p>
                </div>
            </template>
            <div class="form-group" v-if="allowSibDiscount && !allowClassPass">
                <div class="flex align-items-start">
                  <input type="checkbox" v-model="siblingDiscountEnabled" :aria-checked="siblingDiscountEnabled">
                  <div class="m-l-sm">
                    <label class="m-b-none">
                      Do you offer a sibling discount?
                    </label>
                  </div>
                </div>
                <div class="alert alert-warning m-l-sm m-t-sm" v-if="!oneTimeCharge">
                    With installments, the sibling discount is applied to every installment.
                    <!-- todo link to help article -->
                </div>
                <div class="m-l-sm m-t-sm" v-show="siblingDiscountEnabled">
                    <div class="form-group">
                        <label>Discount per additional wrestler</label>
                        <money v-model="discountPerWrestler" v-bind="money" class="form-control"></money>
                    </div>
                    <div class="form-group">
                        <label >After how many siblings should this discount apply?</label>
                        <select v-model="siblingCountForDiscount" class="form-control" name="sibling_discount">
                            <option value="1">1 (every registration except first child gets this discount)</option>
                            <option value="2">2 (3rd child and beyond gets this discount)</option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="form-group">
                <div class="flex align-items-start">
                    <input type="checkbox" name="active" v-model="isActive" :aria-checked="isActive">
                    <div class="m-l-sm">
                        <label for="active" class="m-b-none">
                            Is this payment option active?
                        </label>
                        <p class="text-gray-600">
                            If unchecked, new registrants cannot select this price point. Existing registrations will remain unaffected.
                        </p>
                    </div>
                </div>
            </div>
            <div class="button-area">
                <div v-show="error">
                    <small>
                        <span class="text-danger">{{ errorMessage }}</span>
                    </small>
                </div>
                <button class="btn btn-default" @click.prevent="cancel">Cancel</button>
                <button class="btn btn-primary" @click.prevent="validate">
                    Done
                </button>
            </div>
        </div>
        <div v-show="!expanded">
            <div class="card-bordered"  :class="{ 'opacity-50 bg-gray-50': !isActive }">
                <div class="row">
                    <div class="col-xs-12 col-sm-6">
                        {{ name }}<span class="text-success" v-if="draft"> - Draft</span><span class="text-danger" v-if="!isActive"> - Inactive</span>
                        <template v-if="depositAmount > 0">
                            <div class="text-14 text-gray-600 m-t-xxs">
                                {{ oneTimeCharge ? 'Price' : installmentDates.length + ' installments' }}: {{ formatAsCurrency((oneTimeCharge ? price : installmentTotal) * 100) }}
                            </div>
                            <div class="text-14 text-gray-600">
                                Deposit: {{ formatAsCurrency(depositAmount * 100) }}
                            </div>
                            <div class="text-14 font-semi m-t-xxs">
                                Total: {{ prettyPrice }}
                            </div>
                        </template>
                        <div v-else class="text-14 m-t-xxs">
                            {{ prettyStrategy }} {{ prettyPrice }}
                        </div>
                        <div v-if="allowClassPass" class="text-14 text-gray-600 m-t-xxs">
                            Grants {{ passCount }} class pass<span v-if="passCount > 1">es</span><template v-if="passExpiresInDays > 0">, expiring {{ passExpiresInDays }} day<span v-if="passExpiresInDays > 1">s</span> after purchase</template><template v-else> (no expiry)</template>
                        </div>
                    </div>
                    <div class="col-xs-12 col-sm-6 actions">
                        <a @click.prevent="toggleExpanded">Edit</a>
                        <a v-if="!existingId" class="text-danger" @click.prevent="removeDraft">Discard</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Money } from 'v-money';
import DatePicker from './date_picker.vue';
import { formattingMixin } from './mixins/formatting_mix';

export default {
  name: 'payment-option-form',
  mixins: [formattingMixin],
  components: {
    DatePicker,
    Money,
  },
  props: {
    paymentOption: {
      type: Object,
      required: true,
    },
    allowSibDiscount: {
      type: Boolean,
      default: true,
    },
    allowClassPass: {
      type: Boolean,
      default: false,
    },
    allowDeposit: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    prettyPrice() {
      const price = this.oneTimeCharge ? this.price : this.installmentTotal;
      const total = price + (this.depositAmount > 0 ? this.depositAmount : 0);
      return this.formatAsCurrency(total * 100);
    },
    prettyStrategy() {
      const vm = this;
      const depositStr = vm.depositAmount > 0
        ? `${vm.formatAsCurrency(vm.depositAmount * 100)} deposit + `
        : '';

      if (vm.oneTimeCharge) {
        return `${depositStr}One time charge of`;
      }

      return `${depositStr}${vm.installmentDates.length} installments totaling`;
    },
    oneTimeCharge() {
      return this.paymentStrategy === 'once';
    },
    // Installments with a date set. Entries without a date are considered
    // incomplete and are excluded from totals, counts, and what we send to
    // the backend, so the displayed summary matches what will be persisted.
    validInstallments() {
      return _.filter(this.installmentDates, (i) => i.date !== null);
    },
    installmentTotal() {
      return _.sumBy(this.validInstallments, 'price');
    },
  },
  data() {
    return {
      error: false,
      errorMessage: '',
      draft: false,

      existingId: null,
      name: '',
      price: 0,
      passCount: 1,
      passExpiresInDays: null,
      isActive: true,
      allowedPaymentMethods: 'card',
      paymentStrategy: 'once',
      discountPerWrestler: 0,
      siblingDiscountEnabled: false,
      siblingCountForDiscount: 1,

      depositAmount: 0,

      installmentDates: [],
      clientIdCounter: 0,

      isNewOption: false,

      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false,
      },

      expanded: false,
    };
  },
  mounted() {
    const vm = this;
    // The reactivity on this is fucked. This was one of the very first components I made and this changing of prop info
    // appears to have bad things happen only on Microsoft Edge when replicating sessions. Since this component
    // emits when finished, let's just clone it right out of the gates.
    const po = _.cloneDeep(vm.paymentOption);

    vm.existingId = _.get(po, 'id', null);
    vm.name = _.get(po, 'name', '');
    vm.price = _.get(po, 'price', 0);

    vm.passCount = _.get(po, 'class_pass_quantity', 1);
    // If we receive something that does need a class pass count, but for some reason has no quantity set (_.get will return null if null is set),
    // make sure we at least set it to 1
    if (vm.passCount == null && vm.allowClassPass) {
      vm.passCount = 1;
    }
    vm.passExpiresInDays = _.get(po, 'class_pass_expires_in_days', null);
    // v money expects a float, api returns cents
    if (vm.price > 0) {
      vm.price /= 100.00;
    }
    vm.depositAmount = _.get(po, 'deposit_amount', 0);
    if (vm.depositAmount > 0) {
      vm.depositAmount /= 100.00;
    }
    vm.allowedPaymentMethods = _.get(po, 'allowed_payment_methods', 'card');
    vm.discountPerWrestler = _.get(po, 'discount_per_extra_wrestler', 0);
    if (vm.discountPerWrestler > 0) {
      vm.discountPerWrestler /= 100.00;
    }
    vm.siblingDiscountEnabled = _.get(po, 'sibling_discount_enabled', false);
    vm.siblingCountForDiscount = _.get(po, 'sibling_count_for_discount', 1);
    vm.paymentStrategy = _.get(po, 'payment_strategy', 'once');
    vm.isActive = _.get(po, 'status', 'active') === 'active';
    vm.installmentDates = _.get(po, 'installment_plan.installments', []);
    vm.installmentDates.forEach((date) => {
      date.price /= 100.00;
      if (date.date) {
        // Normalize existing dates to moment objects for date-picker
        date.date = moment(date.date);
      }
      date.clientId = vm.nextClientId();
    });

    if (!vm.existingId) {
      vm.isNewOption = true;
      vm.toggleExpanded();
    }
  },
  methods: {
    nextClientId() {
      this.clientIdCounter += 1;
      return `inst-${this.clientIdCounter}`;
    },
    addNewInstallment() {
      const newInstallment = {
        date: null,
        price: 0,
        clientId: this.nextClientId(),
      };
      this.installmentDates.push(newInstallment);
    },
    removeInstallment(index) {
      this.installmentDates.splice(index, 1);
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.$parent.$emit('editing-payment-option');
    },
    cancel() {
      this.expanded = !this.expanded;
      this.$parent.$emit('editing-payment-option-cancelled', this.paymentOption.newOptionId, this.isNewOption);
    },
    removeDraft() {
      // todo make remove work for existing payment options
      if (!this.existingId) {
        this.$parent.$emit('discard-payment-option-draft', this.paymentOption.newOptionId);
      }
    },
    commitPendingNumberInputs() {
      // Mirror the `.number` modifier (Vue's toNumber): keep the parsed number,
      // fall back to the raw string so downstream validation still catches
      // blanks/decimals rather than silently coercing them. These inputs only
      // render when allowClassPass is set.
      const readNumber = (el) => {
        if (!el) {
          return undefined;
        }
        const parsed = parseFloat(el.value);
        return Number.isNaN(parsed) ? el.value : parsed;
      };
      if (!this.allowClassPass) {
        return;
      }
      const { passCountInput, passExpiresInput } = this.$refs;
      if (passCountInput) {
        this.passCount = readNumber(passCountInput);
      }
      if (passExpiresInput) {
        this.passExpiresInDays = readNumber(passExpiresInput);
      }
    },
    validate() {
      const vm = this;

      // Commit whatever is currently in the class-pass number inputs, even if
      // one still has focus. A stepper tap (or typed value) can be suppressed
      // by v-model's composition guard until the field blurs, so tapping Done
      // without clicking away first would otherwise emit a stale value — most
      // visible on touch.
      vm.commitPendingNumberInputs();

      if (!vm.name) {
        vm.errorMessage = 'Please ensure name is not empty';
        vm.error = true;
        return;
      }

      // Validate that one installment date exists
      if (!vm.oneTimeCharge && _.size(vm.installmentDates) === 0) {
        vm.errorMessage = 'If you want to do an installment plan, you must specify at least one installment date.';
        vm.error = true;
        return;
      }

      // Validate that all installments have dates
      if (!vm.oneTimeCharge && _.includes(_.map(vm.installmentDates, 'date'), null)) {
        vm.errorMessage = 'Once of your installment dates is not set, please ensure all installment dates are entered.';
        vm.error = true;
        return;
      }

      // A payment option can't grant a fractional pass, so require a whole
      // number greater than 0 (not just > 0, which would let 1.5 through).
      if (vm.allowClassPass && (!Number.isInteger(vm.passCount) || vm.passCount < 1)) {
        vm.errorMessage = 'The class pass count must be a whole number greater than 0.';
        vm.error = true;
        return;
      }

      // Guard against decimals/negatives silently becoming "no expiry"
      const expiryProvided = vm.passExpiresInDays !== null
        && vm.passExpiresInDays !== ''
        && vm.passExpiresInDays !== undefined;
      if (vm.allowClassPass && expiryProvided
        && (!Number.isInteger(vm.passExpiresInDays) || vm.passExpiresInDays < 1)) {
        vm.errorMessage = 'Pass expiry must be a whole number of days greater than 0.';
        vm.error = true;
        return;
      }

      vm.expanded = !vm.expanded;
      // This is used to distinguish whether it's completely new or whether it's just updated
      vm.isNewOption = false;
      vm.draft = true;
      vm.$parent.$emit('payment-option-updated', vm.getUpdatedPOObject());
    },
    installDateForApi(id) {
      return {
        date: id.date,
        price: _.round(id.price * 100),
      };
    },
    getUpdatedPOObject() {
      const vm = this;
      if (vm.oneTimeCharge) {
        return {
          draft: true,
          id: vm.existingId,
          newOptionId: vm.paymentOption.newOptionId,
          payment_strategy: 'once',
          installment_plan: {},
          name: vm.name,
          price: _.round(vm.price * 100),
          class_pass_quantity: vm.allowClassPass ? vm.passCount : null,
          class_pass_expires_in_days: vm.allowClassPass && Number.isInteger(vm.passExpiresInDays) && vm.passExpiresInDays > 0 ? vm.passExpiresInDays : null,
          allowed_payment_methods: vm.allowedPaymentMethods,
          discount_per_extra_wrestler: _.round(vm.discountPerWrestler * 100),
          sibling_discount_enabled: vm.siblingDiscountEnabled,
          sibling_count_for_discount: vm.siblingCountForDiscount,
          deposit_amount: vm.depositAmount > 0 ? _.round(vm.depositAmount * 100) : null,
          status: vm.isActive ? 'active' : 'inactive',
        };
      }

      // Installments otherwise. installmentTotal and validInstallments both
      // filter out null-date entries, so the total always matches the sum of
      // the installments we actually send.
      const total = _.round(vm.installmentTotal * 100);
      const installPlan = {
        installments: _.map(vm.validInstallments, vm.installDateForApi),
        total,
      };
      return {
        draft: true,
        id: vm.existingId,
        newOptionId: vm.paymentOption.newOptionId,
        payment_strategy: 'installments',
        installment_plan: installPlan,
        name: vm.name,
        price: total,
        class_pass_quantity: null,
        class_pass_expires_in_days: null,
        allowed_payment_methods: vm.allowedPaymentMethods,
        discount_per_extra_wrestler: _.round(vm.discountPerWrestler * 100),
        sibling_discount_enabled: vm.siblingDiscountEnabled,
        sibling_count_for_discount: vm.siblingCountForDiscount,
        deposit_amount: vm.depositAmount > 0 ? _.round(vm.depositAmount * 100) : null,
        status: vm.isActive ? 'active' : 'inactive',
      };
    },
  },
};
</script>
